export interface StorySectionExtras {
  enabled?: boolean;
  statsEnabled?: boolean;
  statsBackgroundColor?: string;
}

export interface SectionVisibilityExtras {
  enabled?: boolean;
}

export interface SectionSettingsPayload {
  storySection?: StorySectionExtras;
  valuesSection?: SectionVisibilityExtras;
  whyChooseSection?: SectionVisibilityExtras;
  productsSection?: SectionVisibilityExtras;
  solutionsSection?: SectionVisibilityExtras;
}

export interface CorporateSidebarSettings {
  /** JSON içinde saklanır — API sütunu yoksa vitrin buradan okur */
  layoutMode?: 'below_hero' | 'below_navbar';
  backgroundColor?: string;
  borderColor?: string;
  labelColor?: string;
  itemColor?: string;
  itemHoverBackgroundColor?: string;
  itemHoverColor?: string;
  activeBackgroundColor?: string;
  activeTextColor?: string;
  subitemColor?: string;
  subitemActiveColor?: string;
  subitemActiveBackgroundColor?: string;
}

export const DEFAULT_STATS_BACKGROUND_COLOR = 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)';

export const COLOR_PALETTE_PRESETS = [
  { label: 'Mavi', value: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)' },
  { label: 'İndigo', value: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)' },
  { label: 'Mor', value: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' },
  { label: 'Yeşil', value: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
  { label: 'Turuncu', value: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  { label: 'Kırmızı', value: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
  { label: 'Koyu', value: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' },
  { label: 'Açık Gri', value: '#f1f5f9' },
  { label: 'Beyaz', value: '#ffffff' },
] as const;

export const DEFAULT_CORPORATE_SIDEBAR_SETTINGS: Required<CorporateSidebarSettings> = {
  backgroundColor: '#ffffff',
  borderColor: '#e5e7eb',
  labelColor: '#2563eb',
  itemColor: '#374151',
  itemHoverBackgroundColor: '#f9fafb',
  itemHoverColor: '#111827',
  activeBackgroundColor: '#eff6ff',
  activeTextColor: '#1d4ed8',
  subitemColor: '#6b7280',
  subitemActiveColor: '#1d4ed8',
  subitemActiveBackgroundColor: '#eff6ff',
};

export const parseSectionSettingsJson = (raw: unknown): SectionSettingsPayload => {
  if (!raw) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? (parsed as SectionSettingsPayload) : {};
  } catch {
    return {};
  }
};

/** API kaydından sectionSettingsJson / SectionSettingsJson / sectionSettings okur */
export const resolveSectionSettingsFromSource = (
  source: unknown
): SectionSettingsPayload => {
  if (!source || typeof source !== 'object') return {};
  const record = source as Record<string, unknown>;
  if (record.sectionSettings && typeof record.sectionSettings === 'object') {
    return record.sectionSettings as SectionSettingsPayload;
  }
  const raw = record.sectionSettingsJson ?? record.SectionSettingsJson;
  return parseSectionSettingsJson(raw);
};

export const parseCorporateSidebarSettingsJson = (raw: unknown): CorporateSidebarSettings => {
  if (!raw) return { ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') {
      return { ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS };
    }
    const { layoutMode, ...colors } = parsed as CorporateSidebarSettings;
    return {
      ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
      ...(colors as CorporateSidebarSettings),
      ...(layoutMode === 'below_navbar' || layoutMode === 'below_hero' ? { layoutMode } : {}),
    };
  } catch {
    return { ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS };
  }
};

export const serializeSectionSettingsJson = (payload: SectionSettingsPayload): string =>
  JSON.stringify(payload ?? {});

export const serializeCorporateSidebarSettingsJson = (
  payload: CorporateSidebarSettings,
  layoutMode?: 'below_hero' | 'below_navbar'
): string =>
  JSON.stringify({
    ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
    ...payload,
    ...(layoutMode ? { layoutMode } : {}),
  });

export const resolveCorporateSidebarLayoutMode = (
  menuSettings?: {
    corporateSidebarLayoutMode?: string;
    corporateSidebarSettings?: CorporateSidebarSettings;
    corporateSidebarSettingsJson?: string;
  } | null
): 'below_hero' | 'below_navbar' => {
  if (menuSettings?.corporateSidebarLayoutMode === 'below_navbar') {
    return 'below_navbar';
  }
  const fromObject = menuSettings?.corporateSidebarSettings?.layoutMode;
  if (fromObject === 'below_navbar') return 'below_navbar';
  const parsed = parseCorporateSidebarSettingsJson(menuSettings?.corporateSidebarSettingsJson);
  return parsed.layoutMode === 'below_navbar' ? 'below_navbar' : 'below_hero';
};

export const isSectionEnabled = (
  sectionSettings: SectionSettingsPayload,
  sectionKey: keyof SectionSettingsPayload,
  defaultEnabled = true
): boolean => {
  const section = sectionSettings[sectionKey];
  if (!section || section.enabled === undefined) return defaultEnabled;
  return section.enabled !== false;
};
